// src/components/Subscription/UpgradeModal.jsx
// Modal that appears when free users need to upgrade
// ✅ UPDATED: Shows friendly "coming soon" message for native app users

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscription } from '../../hooks/useSubscription';
import { getEarlyAdopterCount, auth } from '../../firebaseClient';
import { loadStripe } from '@stripe/stripe-js';
import { getAlbumTrackingStatus } from '../../utils/albumTracking';
import SignUpModal from '../Auth/SignUpModal';
import './UpgradeModal.css';

// ✅ SAFE: Only initialize Stripe if key exists
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_KEY ? loadStripe(STRIPE_KEY) : null;
const EARLY_ADOPTER_LIMIT = parseInt(process.env.REACT_APP_EARLY_ADOPTER_LIMIT || '1000');

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://vivajour.com';

// ✅ NEW: Detect if running in native Capacitor app (Android or iOS)
function isNativeApp() {
  return !!window?.Capacitor?.isNativePlatform?.();
}

// ✅ Get store name for messaging (kept for potential future use)
function getStoreName() {
  const platform = window?.Capacitor?.getPlatform?.();
  if (platform === 'ios') return 'App Store';
  if (platform === 'android') return 'Play Store';
  return 'app store';
}

export default function UpgradeModal({ isOpen, onClose, context }) {
  // Add this at the top
  useEffect(() => {
    if (isOpen) {
      console.log('[UpgradeModal] Opened with context:', context);
      console.trace('[UpgradeModal] Call stack:'); // This shows WHERE it was called from
    }
  }, [isOpen]);
  const { isAuthenticated, user } = useAuth();
  const { subscription } = useSubscription();
  
  const [loading, setLoading] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const albumStatus = getAlbumTrackingStatus();
  
  // ✅ NEW: Track if we're on native
  const isNative = isNativeApp();
  const storeName = getStoreName();

    // ✅ IMPORTANT: Do not render anything unless the modal is open
  if (!isOpen) return null;


  

  

 

  // ✅ NEW: Handle subscription sync (checks Firestore for existing subscription)
  const handleSyncSubscription = async () => {
    const currentUser = user || auth.currentUser;
    
    if (!currentUser || currentUser.isAnonymous) {
      // Need to sign in first
      setShowSignUp(true);
      return;
    }
    
    setLoading(true);
    try {
      // Check Firestore for existing subscription
      const { getUserSubscription } = await import('../../firebaseClient');
      const subData = await getUserSubscription(currentUser.uid);
      
      if (subData?.subscriptionTier === 'pro' && subData?.subscriptionStatus === 'active') {
        alert('✅ Pro subscription restored!\n\nYour Pro features are now active.');
        onClose();
        // Trigger a refresh of subscription state
        window.location.reload();
      } else {
        alert('No active subscription found for this account.\n\nMake sure you\'re signed in with the same account you used to subscribe.');
      }
    } catch (error) {
      console.error('[Sync] Error checking subscription:', error);
      alert('Unable to check subscription status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Original web upgrade handler (unchanged)
  const handleWebUpgrade = async (freshUser = null) => {
    if (!stripePromise) {
      console.warn('[Upgrade] Stripe not configured - missing REACT_APP_STRIPE_PUBLISHABLE_KEY');
      alert('Payment system not configured yet. Please try again later.');
      return;
    }

    const currentUser = freshUser || user || auth.currentUser;
    const userIsAuthenticated = freshUser ? true : (isAuthenticated || (auth.currentUser && !auth.currentUser.isAnonymous));

    if (!userIsAuthenticated || !currentUser) {
      console.log('[Upgrade] User not authenticated, showing signup');
      setShowSignUp(true);
      return;
    }

    console.log('[Upgrade] User authenticated:', currentUser.uid, currentUser.email);

    try {
      setLoading(true);

      const priceId = process.env.REACT_APP_PRO_PRICE_ID;

      if (!priceId) {
        console.warn('[Upgrade] Price ID not configured');
        alert('Pricing not configured yet. Please try again later.');
        setLoading(false);
        return;
      }

      console.log('[Upgrade] Creating checkout session with:', {
        priceId,
        userId: currentUser.uid,
        email: currentUser.email,
      });

      const response = await fetch(`${API_BASE}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          priceId,
          userId: currentUser.uid,
          email: currentUser.email,
        }),
      });

      if (!response.ok) {
        const errorBody = await response.text().catch(() => '');
        console.error(
          '[Upgrade] Failed to create checkout session:',
          response.status,
          response.statusText,
          errorBody
        );
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      console.log('[Upgrade] Redirecting to checkout:', url);
      window.location.href = url;

    } catch (error) {
      console.error('[Upgrade] Error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Main upgrade handler - routes to sync (native) or web flow
  const handleUpgrade = async (freshUser = null) => {
    if (isNative) {
      // Native apps: only allow sync, no external purchase
      await handleSyncSubscription();
    } else {
      await handleWebUpgrade(freshUser);
    }
  };

  // Different messages based on context
  const getHeaderMessage = () => {
  switch (context) {
    case 'share-link':
      return "Want Longer-Lasting Links?";
    case 'pdf-quality':
      return "Upgrade for Print-Quality PDFs";
    default:
      return "Unlock Pro Features";
  }
};

const getBodyMessage = () => {
  switch (context) {
    case 'share-link':
      return "Your 3-day share link is being created! Upgrade to Pro for unlimited share links that stay active for up to 1 year.";
    case 'pdf-quality':
      return "Free tier includes standard quality (150 DPI) perfect for screens. Upgrade to Pro for print-quality (300 DPI) PDFs with no watermark.";
    default:
      return "Get unlimited share links and print-quality PDFs.";
  }
};

  // Use portal to render at document body level, avoiding stacking context issues
  return ReactDOM.createPortal(
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>×</button>

        <div className="modal-header">
          <h2>{getHeaderMessage()}</h2>
          <p>{getBodyMessage()}</p>
        </div>

        

        {/* ✅ App Store Compliant: No mention of external purchasing */}
        {isNative && (
          <div className="native-notice">
            <div className="notice-icon">🔐</div>
            <div className="notice-content">
              <div className="notice-title">Already a Pro Member?</div>
              <div className="notice-text">
                Sign in with your account to sync your Pro subscription and unlock all features.
              </div>
            </div>
          </div>
        )}

        {/* ✅ Web users: Show full pricing and offer */}
        {!isNative && (
          <div className="offer-box">
            <div className="trial-highlight">🎉 TRY FREE FOR 30 DAYS</div>
            <div className="offer-badge">Limited Time Offer</div>
            <div className="offer-price">
              <span className="original-price">$39.99</span>
              <span className="big-price">$19.99</span>
              <span className="period">/year</span>
            </div>
            <div className="savings-badge">Save 50% — Limited Time Only!</div>
            <p className="offer-details">No charge for 30 days • Cancel anytime</p>
            
            <div className="offer-features">
              <div className="feature">✅ Unlimited albums</div>
              <div className="feature">✅ Unlimited share links (1 year)</div>
              <div className="feature">✅ Print-quality PDF (300 DPI)</div>
              <div className="feature">✅ Premium templates</div>
              <div className="feature">✅ Priority support</div>
            </div>

            <p className="tip">
              💡 Photos stay on YOUR device. Own your PDFs forever.
            </p>
          </div>
        )}

        {/* ✅ Native users: Show features only, no pricing (App Store compliant) */}
        {isNative && (
          <div className="offer-box">
            <div className="trial-highlight">✨ Pro Features</div>
            
            <div className="offer-features">
              <div className="feature">✅ Unlimited albums</div>
              <div className="feature">✅ Unlimited share links (1 year)</div>
              <div className="feature">✅ Print-quality PDF (300 DPI)</div>
              <div className="feature">✅ Premium templates</div>
              <div className="feature">✅ Priority support</div>
            </div>

            <p className="tip">
              💡 Photos stay on YOUR device. Own your PDFs forever.
            </p>
          </div>
        )}

        <div className="modal-actions">
          <button
            className="upgrade-btn upgrade-btn-primary"
            onClick={() => handleUpgrade()}
            disabled={loading}
          >
            {loading ? 'Checking...' : (isNative ? (isAuthenticated ? '🔄 Sync Subscription' : '🔐 Sign In to Sync') : 'Start My Free 30-Day Trial')}
          </button>

          <button className="upgrade-btn upgrade-btn-text" onClick={onClose}>
            {context === 'share-link' ? 'Got it' : 'Continue with Free'}
          </button>
        </div>

        {/* Footer text */}
        {!showSignUp && (
          <p className="terms">
            {isNative ? (
              <>Sign in to sync your existing subscription</>
            ) : isAuthenticated ? (
              <>Cancel anytime—no questions asked</>
            ) : (
              <>Create an account to continue</>
            )}
          </p>
        )}

        {/* Sign Up Modal - needed for both web and native (for sync) */}
        <SignUpModal
          isOpen={showSignUp}
          onClose={() => setShowSignUp(false)}
          onSuccess={(newUser) => {
            console.log('[Upgrade] SignUp successful');
            setShowSignUp(false);
            const freshUser = auth.currentUser;
            if (isNative) {
              // Native: After sign in, sync subscription
              handleSyncSubscription();
            } else {
              // Web: After sign in, proceed to checkout
              handleWebUpgrade(freshUser);
            }
          }}
        />

      </div>
    </div>,
    document.body
  );
}